import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from './DashboardLayout';
import Select from 'react-select';
import ConfirmationModal from './ConfirmationModal';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { adminReportsApi } from '../lib/api';
import { extractErrorMessage } from '../lib/utils';
import { useAuth } from '../lib/AuthContext';
import './AdminReportsManagement.css';

interface Report {
    id: number;
    title: string;
    description: string;
    content: string;
    status: 'draft' | 'published' | 'archived';
    platform?: string;
    threatType?: string;
    postedBy?: string;
    dateDetected?: string;
    affectedCompany?: string;
    dataType?: string;
    proofShared: boolean;
    threatScore?: number;
    tags?: string[];
    downloadUrl?: string;
    views: number;
    downloads: number;
    createdAt: string;
    updatedAt: string;
    createdBy?: {
        id: number;
        name: string;
        email?: string;
    };
    created_by_name?: string;
}

const AdminReportsManagement: React.FC = () => {
    // Auth context
    const { hasPermission, isSuperAdmin } = useAuth();
    
    // Alert system hooks
    const showSuccess = useSuccessAlert();
    const showError = useErrorAlert();
    const showWarning = useWarningAlert();

    const [allReports, setAllReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [platformFilter, setPlatformFilter] = useState('all');
    const [threatTypeFilter, setThreatTypeFilter] = useState('all');
    const [selectedReports, setSelectedReports] = useState<number[]>([]);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        content: '',
        status: 'draft' as Report['status'],
        platform: '',
        threatType: '',
        postedBy: '',
        dateDetected: '',
        affectedCompany: '',
        dataType: '',
        proofShared: false,
        threatScore: 5,
        tags: [] as string[],
        downloadUrl: ''
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const reportsPerPage = 10;

    // React-select options - these will be loaded from the API
    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'archived', label: 'Archived' }
    ];

    const [platformOptions, setPlatformOptions] = useState([
        { value: 'all', label: 'All Platforms' }
    ]);

    const [threatTypeOptions, setThreatTypeOptions] = useState([
        { value: 'all', label: 'All Threat Types' }
    ]);

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Client-side filtering for better responsiveness
    const filteredReports = useMemo(() => {
        let filtered = allReports;

        // Apply search filter
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            filtered = filtered.filter(report =>
                report.title.toLowerCase().includes(query) ||
                report.description?.toLowerCase().includes(query) ||
                report.content?.toLowerCase().includes(query) ||
                report.affectedCompany?.toLowerCase().includes(query) ||
                report.platform?.toLowerCase().includes(query)
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(report => report.status === statusFilter);
        }

        // Apply platform filter
        if (platformFilter !== 'all') {
            filtered = filtered.filter(report => report.platform === platformFilter);
        }

        // Apply threat type filter
        if (threatTypeFilter !== 'all') {
            filtered = filtered.filter(report => report.threatType === threatTypeFilter);
        }

        return filtered;
    }, [allReports, debouncedSearchQuery, statusFilter, platformFilter, threatTypeFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredReports.length / reportsPerPage);
    const startIndex = (currentPage - 1) * reportsPerPage;
    const paginatedReports = filteredReports.slice(startIndex, startIndex + reportsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, statusFilter, platformFilter, threatTypeFilter]);

    // Initial data fetch
    useEffect(() => {
        const fetchOptionsAndReports = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch options first
                try {
                    const optionsResponse = await adminReportsApi.getOptions();
                    if (optionsResponse.success && optionsResponse.data) {
                        const platforms = [{ value: 'all', label: 'All Platforms' }, ...optionsResponse.data.platforms];
                        const threatTypes = [{ value: 'all', label: 'All Threat Types' }, ...optionsResponse.data.threatTypes];
                        
                        setPlatformOptions(platforms);
                        setThreatTypeOptions(threatTypes);
                    }
                } catch (optionsError) {
                    console.error('Error fetching options:', optionsError);
                    // Use fallback options if API fails
                    setPlatformOptions([
                        { value: 'all', label: 'All Platforms' },
                        { value: 'TELEGRAM', label: 'Telegram' },
                        { value: 'DISCORD', label: 'Discord' },
                        { value: 'DARKWEB', label: 'Dark Web' },
                        { value: 'FORUM', label: 'Forum' },
                        { value: 'SOCIAL_MEDIA', label: 'Social Media' },
                        { value: 'MARKETPLACE', label: 'Marketplace' },
                        { value: 'EMAIL', label: 'Email' },
                        { value: 'WEBSITE', label: 'Website' },
                        { value: 'OTHER', label: 'Other' }
                    ]);
                    setThreatTypeOptions([
                        { value: 'all', label: 'All Threat Types' },
                        { value: 'DATA_BREACH', label: 'Data Breach' },
                        { value: 'MALWARE', label: 'Malware' },
                        { value: 'PHISHING', label: 'Phishing' },
                        { value: 'RANSOMWARE', label: 'Ransomware' },
                        { value: 'CREDENTIAL_THEFT', label: 'Credential Theft' },
                        { value: 'FINANCIAL_FRAUD', label: 'Financial Fraud' },
                        { value: 'VULNERABILITY', label: 'Vulnerability' },
                        { value: 'SOCIAL_ENGINEERING', label: 'Social Engineering' },
                        { value: 'DDOS_ATTACK', label: 'DDoS Attack' },
                        { value: 'INSIDER_THREAT', label: 'Insider Threat' },
                        { value: 'APT', label: 'Advanced Persistent Threat' },
                        { value: 'OTHER', label: 'Other' }
                    ]);
                }

                // Fetch reports
                const response = await adminReportsApi.getReports({ limit: 1000 }); // Fetch all reports for client-side filtering

                if (response.success && response.data) {
                    setAllReports(response.data.reports || []);
                } else {
                    throw new Error(extractErrorMessage(response));
                }
            } catch (error) {
                console.error('Error fetching reports:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch reports');
                showError(error instanceof Error ? error.message : 'Failed to fetch reports', 'Error Loading Reports');
                setAllReports([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOptionsAndReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Optimistic update helper
    const updateReportOptimistically = useCallback((reportId: number, updates: Partial<Report>) => {
        setAllReports(prev => prev.map(report => 
            report.id === reportId ? { ...report, ...updates } : report
        ));
    }, []);

    // Add new report optimistically
    const addReportOptimistically = useCallback((newReport: Report) => {
        setAllReports(prev => [newReport, ...prev]);
    }, []);

    // Remove report optimistically
    const removeReportOptimistically = useCallback((reportId: number) => {
        setAllReports(prev => prev.filter(report => report.id !== reportId));
    }, []);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        // Ensure we're displaying in the user's local timezone
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        // Calculate difference in UTC to avoid timezone issues
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getStatusColor = (status: string) => {
        const normalizedStatus = status?.toLowerCase();
        switch (normalizedStatus) {
            case 'draft': return '#f59e0b';
            case 'published': return '#10b981';
            case 'archived': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getThreatScoreColor = (score?: number) => {
        if (!score) return '#6b7280';
        if (score >= 8) return '#ef4444';
        if (score >= 6) return '#f59e0b';
        if (score >= 4) return '#3b82f6';
        return '#10b981';
    };

    const getPlatformIcon = (platform?: string) => {
        switch (platform?.toUpperCase()) {
            case 'TELEGRAM': return 'ri-telegram-line';
            case 'DISCORD': return 'ri-discord-line';
            case 'DARKWEB': 
            case 'DARK WEB': return 'ri-spy-line';
            case 'FORUM':
            case 'FORUMS': return 'ri-discuss-line';
            case 'MARKETPLACE': return 'ri-store-line';
            case 'SOCIAL_MEDIA':
            case 'SOCIAL MEDIA': return 'ri-share-line';
            case 'EMAIL': return 'ri-mail-line';
            case 'WEBSITE': return 'ri-global-line';
            case 'OTHER': return 'ri-file-text-line';
            default: return 'ri-file-text-line';
        }
    };

    const handleSelectReport = (reportId: number) => {
        setSelectedReports(prev =>
            prev.includes(reportId)
                ? prev.filter(id => id !== reportId)
                : [...prev, reportId]
        );
    };

    const handleSelectAll = () => {
        if (selectedReports.length === paginatedReports.length) {
            setSelectedReports([]);
        } else {
            setSelectedReports(paginatedReports.map(report => report.id));
        }
    };

    const handleBulkStatusChange = async (newStatus: string) => {
        if (!newStatus || selectedReports.length === 0) return;

        setUpdating(true);
        const selectedCount = selectedReports.length;
        
        // Optimistic update
        const originalReports = [...allReports];
        selectedReports.forEach(reportId => {
            updateReportOptimistically(reportId, { status: newStatus as Report['status'] });
        });

        try {
            // Update each report individually since there's no bulk update API
            const updatePromises = selectedReports.map(reportId => 
                adminReportsApi.updateReport(reportId.toString(), { status: newStatus as 'draft' | 'published' | 'archived' })
            );

            const results = await Promise.allSettled(updatePromises);
            const failures = results.filter(result => result.status === 'rejected');

            if (failures.length > 0) {
                // Revert optimistic update on failures
                setAllReports(originalReports);
                throw new Error(`Failed to update ${failures.length} report(s)`);
            }

            setSelectedReports([]);
            showSuccess(
                `Successfully updated ${selectedCount} report${selectedCount > 1 ? 's' : ''} to ${newStatus}`,
                'Bulk Update Complete'
            );
        } catch (error) {
            console.error('Error updating reports:', error);
            setError(error instanceof Error ? error.message : 'Failed to update reports');
            showError(
                error instanceof Error ? error.message : 'Failed to update reports',
                'Bulk Update Failed'
            );
            // Revert optimistic update on error
            setAllReports(originalReports);
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteReport = async (report: Report) => {
        setReportToDelete(report);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteReport = async () => {
        if (!reportToDelete) return;

        setUpdating(true);
        
        // Optimistic update
        removeReportOptimistically(reportToDelete.id);

        try {
            const response = await adminReportsApi.deleteReport(reportToDelete.id.toString());

            if (!response.success) {
                // Revert optimistic update on failure
                setAllReports(prev => [reportToDelete, ...prev]);
                throw new Error(extractErrorMessage(response));
            }
            
            showSuccess(`Report "${reportToDelete.title}" has been deleted successfully`, 'Report Deleted');
        } catch (error) {
            console.error('Error deleting report:', error);
            setError(error instanceof Error ? error.message : 'Failed to delete report');
            showError(
                error instanceof Error ? error.message : 'Failed to delete report',
                'Error Deleting Report'
            );
            // Revert optimistic update on error
            setAllReports(prev => [reportToDelete, ...prev]);
        } finally {
            setUpdating(false);
            setIsDeleteModalOpen(false);
            setReportToDelete(null);
        }
    };

    const handleEditReport = (report: Report) => {
        setSelectedReport(report);
        setFormData({
            title: report.title,
            description: report.description || '',
            content: report.content,
            status: report.status,
            platform: report.platform || '',
            threatType: report.threatType || '',
            postedBy: report.postedBy || '',
            dateDetected: report.dateDetected || '',
            affectedCompany: report.affectedCompany || '',
            dataType: report.dataType || '',
            proofShared: report.proofShared,
            threatScore: typeof report.threatScore === 'string' ? parseFloat(report.threatScore) || 0 : (report.threatScore || 0),
            tags: report.tags || [],
            downloadUrl: report.downloadUrl || ''
        });
        setFormErrors({});
        setIsEditModalOpen(true);
    };

    const handleCreateReport = () => {
        setSelectedReport(null);
        setFormData({
            title: '',
            description: '',
            content: '',
            status: 'draft',
            platform: '',
            threatType: '',
            postedBy: '',
            dateDetected: '',
            affectedCompany: '',
            dataType: '',
            proofShared: false,
            threatScore: 5,
            tags: [],
            downloadUrl: ''
        });
        setFormErrors({});
        setIsCreateModalOpen(true);
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        } else if (formData.title.length > 200) {
            errors.title = 'Title must be less than 200 characters';
        }

        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        } else if (formData.description.length > 2000) {
            errors.description = 'Description must be less than 2000 characters';
        }

        if (!formData.content.trim()) {
            errors.content = 'Content is required';
        }

        if (formData.threatScore !== undefined && (formData.threatScore < 0 || formData.threatScore > 10)) {
            errors.threatScore = 'Threat score must be between 0 and 10';
        }

        setFormErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            showWarning('Please fix the form errors before submitting', 'Form Validation Failed');
            return false;
        }
        
        return true;
    };

    const handleSubmitForm = async (isEdit: boolean) => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        
        try {
            const submitData = {
                title: formData.title,
                description: formData.description,
                content: formData.content,
                status: formData.status,
                platform: formData.platform || undefined,
                threatType: formData.threatType || undefined,
                postedBy: formData.postedBy || undefined,
                dateDetected: formData.dateDetected || undefined,
                affectedCompany: formData.affectedCompany || undefined,
                dataType: formData.dataType || undefined,
                proofShared: formData.proofShared,
                threatScore: formData.threatScore,
                tags: formData.tags,
                downloadUrl: formData.downloadUrl || undefined
            };

            if (isEdit && selectedReport) {
                // Optimistic update for edit
                updateReportOptimistically(selectedReport.id, {
                    title: formData.title,
                    description: formData.description,
                    content: formData.content,
                    status: formData.status,
                    platform: formData.platform,
                    threatType: formData.threatType,
                    postedBy: formData.postedBy,
                    dateDetected: formData.dateDetected,
                    affectedCompany: formData.affectedCompany,
                    dataType: formData.dataType,
                    proofShared: formData.proofShared,
                    threatScore: formData.threatScore,
                    downloadUrl: formData.downloadUrl
                });
                
                const response = await adminReportsApi.updateReport(selectedReport.id.toString(), submitData);
                
                if (!response.success) {
                    // Revert optimistic update on failure
                    updateReportOptimistically(selectedReport.id, selectedReport);
                    throw response; // Throw the entire response object
                }
                
                showSuccess(`Report "${formData.title}" has been updated successfully`, 'Report Updated');
            } else {
                const response = await adminReportsApi.createReport(submitData);
                
                if (response.success && response.data && response.data.report) {
                    // Use the complete report object returned from the API
                    const reportData = response.data.report;
                    const newReport: Report = {
                        id: reportData.id,
                        title: reportData.title,
                        description: reportData.description,
                        content: reportData.content,
                        status: reportData.status,
                        platform: reportData.platform,
                        threatType: reportData.threatType,
                        postedBy: reportData.postedBy,
                        dateDetected: reportData.dateDetected,
                        affectedCompany: reportData.affectedCompany,
                        dataType: reportData.dataType,
                        proofShared: reportData.proofShared,
                        threatScore: reportData.threatScore,
                        tags: reportData.tags,
                        downloadUrl: reportData.downloadUrl,
                        views: reportData.views || 0,
                        downloads: reportData.downloads || 0,
                        createdAt: reportData.createdAt,
                        updatedAt: reportData.updatedAt,
                        createdBy: reportData.createdBy,
                        created_by_name: reportData.createdBy?.name
                    };
                    
                    addReportOptimistically(newReport);
                    setCurrentPage(1); // Go to first page to see new report
                    showSuccess(`Report "${reportData.title}" has been created successfully`, 'Report Created');
                } else {
                    throw response; // Throw the entire response object
                }
            }

            // Close modals
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedReport(null);
            
            // Reset form
            setFormData({
                title: '',
                description: '',
                content: '',
                status: 'draft',
                platform: '',
                threatType: '',
                postedBy: '',
                dateDetected: '',
                affectedCompany: '',
                dataType: '',
                proofShared: false,
                threatScore: 5,
                tags: [],
                downloadUrl: ''
            });
            setFormErrors({});
        } catch (error) {
            console.log('Caught error:', error);
            console.error(`Error ${isEdit ? 'updating' : 'creating'} report:`, error);

            const err = error as any;
            console.log('err:', err);
            console.log('err.data:', err?.data);
            console.log('err.errors:', err?.errors);
            console.log('err.data?.errors:', err?.data?.errors);
            console.log('err.message:', err?.message);
            
            let errorMsg = 'Unknown error occurred';
            
            // Check if error has errors array at top level (from API layer)
            if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
                const firstError = err.errors[0];
                if (firstError?.message) {
                    errorMsg = firstError.message;
                }
            }
            // Check if error is an API response object (thrown directly)
            else if (err?.data?.errors && Array.isArray(err.data.errors) && err.data.errors.length > 0) {
                const firstError = err.data.errors[0];
                if (firstError?.message) {
                    errorMsg = firstError.message;
                }
            } 
            // Check if error is a standard Error object with attached data
            else if (err?.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
                const firstError = err.response.data.errors[0];
                if (firstError?.message) {
                    errorMsg = firstError.message;
                }
            }
            // Check for general API response message
            else if (err?.message) {
                errorMsg = err.message;
            } 
            // Check for response message
            else if (err?.response?.message) {
                errorMsg = err.response.message;
            }
            else {
                errorMsg = `Failed to ${isEdit ? 'update' : 'create'} report`;
            }
            
            console.log('Final errorMsg:', errorMsg);

            showError(
                errorMsg,
                `Error ${isEdit ? 'Updating' : 'Creating'} Report`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout title="Reports Management">
                <div className="admin-alerts-loading">
                    <div className="admin-alerts-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="admin-alerts-loading-text">Loading reports...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Reports Management">
                <div className="admin-alerts-error">
                    <div className="admin-alerts-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="admin-alerts-error-content">
                        <h3>Failed to Load Reports</h3>
                        <p>{error}</p>
                        <button
                            className="admin-alerts-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Reports Management">
            <div className="admin-alerts">
                {/* Header */}
                <div className="admin-alerts-header">
                    <div className="admin-alerts-header-content">
                        <h1 className="admin-alerts-title">Reports Management</h1>
                        <p className="admin-alerts-subtitle">
                            Manage threat intelligence reports, security findings, and data breach information across your organization
                        </p>
                    </div>
                    <div className="admin-alerts-stats">
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">{allReports.length}</div>
                            <div className="admin-alerts-stat-label">Total</div>
                        </div>
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">
                                {allReports.filter((r: Report) => r.status === 'published').length}
                            </div>
                            <div className="admin-alerts-stat-label">Published</div>
                        </div>
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">
                                {allReports.filter((r: Report) => r.status === 'draft').length}
                            </div>
                            <div className="admin-alerts-stat-label">Draft</div>
                        </div>
                    </div>
                </div>

                {/* Filters & Actions */}
                <div className={`admin-alerts-filters ${updating ? 'updating' : ''}`}>
                    <div className="admin-alerts-filters-row">
                        <div className="admin-alerts-search-container">
                            <div className="admin-alerts-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search reports by title, description, content, or company..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`admin-alerts-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="admin-alerts-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="admin-alerts-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="admin-alerts-actions">
                            {(hasPermission('reports_create') || isSuperAdmin()) && (
                                <button
                                    className="admin-alerts-create-btn"
                                    onClick={handleCreateReport}
                                >
                                    <i className="ri-add-line"></i>
                                    <span>New Report</span>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="admin-alerts-filters-secondary">
                        <div className="admin-alerts-filter-group">
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(option => option.value === statusFilter)}
                                    onChange={(option) => setStatusFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Platform</label>
                                <Select
                                    options={platformOptions}
                                    value={platformOptions.find(option => option.value === platformFilter)}
                                    onChange={(option) => setPlatformFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Threat Type</label>
                                <Select
                                    options={threatTypeOptions}
                                    value={threatTypeOptions.find(option => option.value === threatTypeFilter)}
                                    onChange={(option) => setThreatTypeFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-actions">
                                <button
                                    className="admin-alerts-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setStatusFilter('all');
                                        setPlatformFilter('all');
                                        setThreatTypeFilter('all');
                                    }}
                                    title="Clear all filters"
                                    disabled={updating}
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Actions */}
                {selectedReports.length > 0 && (
                    <div className="admin-alerts-bulk-actions">
                        <div className="admin-alerts-bulk-info">
                            <span>{selectedReports.length} report(s) selected</span>
                        </div>
                        <div className="admin-alerts-bulk-buttons">
                            <Select
                                options={[
                                    { value: 'draft', label: 'Mark as Draft' },
                                    { value: 'published', label: 'Mark as Published' },
                                    { value: 'archived', label: 'Mark as Archived' }
                                ]}
                                value={null}
                                onChange={(option) => handleBulkStatusChange(option?.value || '')}
                                placeholder="Change Status"
                                styles={selectStyles}
                                isSearchable={false}
                            />
                            <button
                                className="admin-alerts-bulk-btn admin-alerts-bulk-clear"
                                onClick={() => {
                                    setSelectedReports([]);
                                }}
                            >
                                <i className="ri-close-line"></i>
                                Clear Selection
                            </button>
                        </div>
                    </div>
                )}

                {/* Reports List */}
                <div className="admin-alerts-list-container">
                    {updating && (
                        <div className="admin-alerts-updating-indicator">
                            <i className="ri-loader-4-line rotating"></i>
                            <span>Updating reports...</span>
                        </div>
                    )}
                    {paginatedReports.length === 0 ? (
                        <div className="admin-alerts-empty">
                            <i className="ri-file-text-line"></i>
                            <h3>No reports found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className={`admin-alerts-list ${updating ? 'updating' : ''}`}>
                            <div className="admin-alerts-list-header">
                                <label className="admin-alerts-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedReports.length === paginatedReports.length}
                                        onChange={handleSelectAll}
                                    />
                                    <div className="admin-alerts-checkbox-checkmark">
                                        <i className="ri-check-line"></i>
                                    </div>
                                </label>
                                <div className="admin-alerts-list-header-title">Report</div>
                                <div className="admin-alerts-list-header-title">Status</div>
                                <div className="admin-alerts-list-header-title">Platform</div>
                                <div className="admin-alerts-list-header-title">Threat Score</div>
                                <div className="admin-alerts-list-header-title">Created</div>
                                <div className="admin-alerts-list-header-title">Actions</div>
                            </div>
                            {paginatedReports.map((report) => (
                                <div key={report.id} className="admin-alert-card">
                                    <label className="admin-alerts-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedReports.includes(report.id)}
                                            onChange={() => handleSelectReport(report.id)}
                                        />
                                        <div className="admin-alerts-checkbox-checkmark">
                                            <i className="ri-check-line"></i>
                                        </div>
                                    </label>
                                    <div className="admin-alert-main">
                                        <div className="admin-alert-icon">
                                            <i className={getPlatformIcon(report.platform)}></i>
                                        </div>
                                        <div className="admin-alert-content">
                                            <h4 className="admin-alert-title">{report.title}</h4>
                                            <p className="admin-alert-description">{report.description}</p>
                                            <div className="admin-alert-meta">
                                                {report.affectedCompany && (
                                                    <span className="admin-alert-source">
                                                        <i className="ri-building-line"></i>
                                                        {report.affectedCompany}
                                                    </span>
                                                )}
                                                {report.created_by_name && (
                                                    <span className="admin-alert-assigned">
                                                        <i className="ri-user-line"></i>
                                                        {report.created_by_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="admin-alert-status">
                                        <span
                                            className="admin-alert-status-badge"
                                            style={{
                                                backgroundColor: `${getStatusColor(report.status)}20`,
                                                color: getStatusColor(report.status)
                                            }}
                                        >
                                            {report.status.charAt(0).toUpperCase() + report.status.slice(1).toLowerCase()}
                                        </span>
                                    </div>
                                    <div className="admin-alert-category">
                                        <span className="admin-alert-category-badge">
                                            {report.platform?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="admin-alert-severity">
                                        <span
                                            className="admin-alert-severity-badge"
                                            style={{
                                                backgroundColor: `${getThreatScoreColor(report.threatScore)}20`,
                                                color: getThreatScoreColor(report.threatScore)
                                            }}
                                        >
                                            {report.threatScore || 'N/A'}/10
                                        </span>
                                    </div>
                                    <div className="admin-alert-time">
                                        <span className="admin-alert-time-main">{getTimeAgo(report.createdAt)}</span>
                                        <span className="admin-alert-time-full">{formatDate(report.createdAt)}</span>
                                    </div>
                                    <div className="admin-alert-actions">
                                        {(hasPermission('reports_edit') || isSuperAdmin()) && (
                                            <button
                                                className="admin-alert-action-btn"
                                                onClick={() => handleEditReport(report)}
                                                title="Edit report"
                                            >
                                                <i className="ri-edit-line"></i>
                                            </button>
                                        )}
                                        {(hasPermission('reports_delete') || isSuperAdmin()) && (
                                            <button
                                                className="admin-alert-action-btn admin-alert-delete-btn"
                                                onClick={() => handleDeleteReport(report)}
                                                title="Delete report"
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className={`admin-alerts-pagination ${updating ? 'updating' : ''}`}>
                        <button
                            className="admin-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1 || updating}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="admin-alerts-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredReports.length} reports)
                        </div>
                        <button
                            className="admin-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages || updating}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Create Modal */}
                {isCreateModalOpen && (
                    <div className="admin-alerts-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                        <div className="admin-alerts-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-alerts-modal-header">
                                <div className="admin-alerts-modal-header-content">
                                    <h3>Create New Report</h3>
                                    <p>Add a new threat intelligence report with detailed security findings</p>
                                </div>
                                <button onClick={() => setIsCreateModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-alerts-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(false); }}>
                                    <div className="admin-alerts-form-section">
                                        <h4>Report Details</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Enter a descriptive report title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Description *</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Brief description of the threat report"
                                                rows={3}
                                                className={formErrors.description ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Content *</label>
                                            <textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Detailed report content, analysis, and findings"
                                                rows={6}
                                                className={formErrors.content ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.content && <span className="error-text">{formErrors.content}</span>}
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Classification</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Platform</label>
                                                <Select
                                                    value={platformOptions.slice(1).find(option => option.value === formData.platform)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, platform: selectedOption.value })}
                                                    options={platformOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select platform"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Threat Type</label>
                                                <Select
                                                    value={threatTypeOptions.slice(1).find(option => option.value === formData.threatType)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, threatType: selectedOption.value })}
                                                    options={threatTypeOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select threat type"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Threat Score (0-10)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="10"
                                                    step="0.01"
                                                    value={formData.threatScore?.toFixed(2) || "0.00"}
                                                    onChange={(e) => setFormData({ ...formData, threatScore: e.target.value ? parseFloat(parseFloat(e.target.value).toFixed(2)) : 0 })}
                                                    disabled={isSubmitting}
                                                />
                                                {formErrors.threatScore && <span className="error-text">{formErrors.threatScore}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Additional Information</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Posted By</label>
                                                <input
                                                    type="text"
                                                    value={formData.postedBy}
                                                    onChange={(e) => setFormData({ ...formData, postedBy: e.target.value })}
                                                    placeholder="Who posted the original threat"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Affected Company</label>
                                                <input
                                                    type="text"
                                                    value={formData.affectedCompany}
                                                    onChange={(e) => setFormData({ ...formData, affectedCompany: e.target.value })}
                                                    placeholder="Company or organization affected"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Data Type</label>
                                                <input
                                                    type="text"
                                                    value={formData.dataType}
                                                    onChange={(e) => setFormData({ ...formData, dataType: e.target.value })}
                                                    placeholder="Type of data involved (PII, financial, etc.)"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Download URL</label>
                                                <input
                                                    type="url"
                                                    value={formData.downloadUrl}
                                                    onChange={(e) => setFormData({ ...formData, downloadUrl: e.target.value })}
                                                    placeholder="https://example.com/report.pdf"
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label className="admin-alerts-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.proofShared}
                                                    onChange={(e) => setFormData({ ...formData, proofShared: e.target.checked })}
                                                    disabled={isSubmitting}
                                                />
                                                <div className="admin-alerts-checkbox-checkmark">
                                                    <i className="ri-check-line"></i>
                                                </div>
                                                Proof Shared
                                            </label>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsCreateModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Creating Report...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-add-line"></i>
                                                    Create Report
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {isEditModalOpen && selectedReport && (
                    <div className="admin-alerts-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                        <div className="admin-alerts-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-alerts-modal-header">
                                <div className="admin-alerts-modal-header-content">
                                    <h3>Edit Report</h3>
                                    <p>Update report information and status</p>
                                </div>
                                <button onClick={() => setIsEditModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-alerts-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(true); }}>
                                    <div className="admin-alerts-form-section">
                                        <h4>Report Details</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Enter a descriptive report title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Description *</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Brief description of the threat report"
                                                rows={3}
                                                className={formErrors.description ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Content *</label>
                                            <textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Detailed report content, analysis, and findings"
                                                rows={6}
                                                className={formErrors.content ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.content && <span className="error-text">{formErrors.content}</span>}
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Classification</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Platform</label>
                                                <Select
                                                    value={platformOptions.slice(1).find(option => option.value === formData.platform)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, platform: selectedOption.value })}
                                                    options={platformOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select platform"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Threat Type</label>
                                                <Select
                                                    value={threatTypeOptions.slice(1).find(option => option.value === formData.threatType)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, threatType: selectedOption.value })}
                                                    options={threatTypeOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select threat type"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Threat Score (0-10)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="10"
                                                    step="0.01"
                                                    value={formData.threatScore?.toFixed(2) || "0.00"}
                                                    onChange={(e) => setFormData({ ...formData, threatScore: e.target.value ? parseFloat(parseFloat(e.target.value).toFixed(2)) : 0 })}
                                                    disabled={isSubmitting}
                                                />
                                                {formErrors.threatScore && <span className="error-text">{formErrors.threatScore}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Additional Information</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Posted By</label>
                                                <input
                                                    type="text"
                                                    value={formData.postedBy}
                                                    onChange={(e) => setFormData({ ...formData, postedBy: e.target.value })}
                                                    placeholder="Who posted the original threat"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Affected Company</label>
                                                <input
                                                    type="text"
                                                    value={formData.affectedCompany}
                                                    onChange={(e) => setFormData({ ...formData, affectedCompany: e.target.value })}
                                                    placeholder="Company or organization affected"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Data Type</label>
                                                <input
                                                    type="text"
                                                    value={formData.dataType}
                                                    onChange={(e) => setFormData({ ...formData, dataType: e.target.value })}
                                                    placeholder="Type of data involved (PII, financial, etc.)"
                                                    disabled={isSubmitting}
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Download URL</label>
                                                <input
                                                    type="url"
                                                    value={formData.downloadUrl}
                                                    onChange={(e) => setFormData({ ...formData, downloadUrl: e.target.value })}
                                                    placeholder="https://example.com/report.pdf"
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label className="admin-alerts-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.proofShared}
                                                    onChange={(e) => setFormData({ ...formData, proofShared: e.target.checked })}
                                                    disabled={isSubmitting}
                                                />
                                                <div className="admin-alerts-checkbox-checkmark">
                                                    <i className="ri-check-line"></i>
                                                </div>
                                                Proof Shared
                                            </label>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Updating Report...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-line"></i>
                                                    Update Report
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDeleteReport}
                    title="Delete Report"
                    message="Are you sure you want to delete this report? This action cannot be undone."
                    confirmText="Delete Report"
                    type="danger"
                    isLoading={updating}
                >
                    {reportToDelete && (
                        <div className="admin-alerts-delete-modal-alert">
                            <div className="admin-alerts-delete-modal-alert-icon">
                                <i className={getPlatformIcon(reportToDelete.platform)}></i>
                            </div>
                            <div className="admin-alerts-delete-modal-alert-info">
                                <h4>{reportToDelete.title}</h4>
                                <p>{reportToDelete.description}</p>
                                <div className="admin-alerts-delete-modal-alert-meta">
                                    <span className="admin-alerts-delete-modal-alert-priority" 
                                          style={{ color: getStatusColor(reportToDelete.status) }}>
                                        {reportToDelete.status.toLowerCase()}
                                    </span>
                                    <span className="admin-alerts-delete-modal-alert-category">
                                        {reportToDelete.platform || 'Unknown'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </ConfirmationModal>
            </div>
        </DashboardLayout>
    );
};

export default AdminReportsManagement;
